import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { PixType } from '../../enums';
import { usePixKeys } from '../usePixKeys';
export function usePixKeyTypes(props) {
    const { t } = useTranslation();
    const { unmaskCnpj } = useCnpjAlphanumeric();
    const [selectedType, setSelectedType] = useState(undefined);
    const { pixKeys: fallbackPixKeys, getPixKeys } = usePixKeys();
    const accountInfo = getSelectedAccountInfo();
    const userDocument = accountInfo?.documentNumber;
    const pixKeys = props?.pixKeys || fallbackPixKeys;
    useEffect(() => {
        if (!pixKeys && !props?.pixKeys) {
            getPixKeys();
        }
    }, [getPixKeys, pixKeys, props?.pixKeys]);
    const hasTaxIdentifierKey = useMemo(() => {
        if (!pixKeys || !userDocument) {
            return false;
        }
        return pixKeys.some(pixKey => {
            if (pixKey.type !== PixType.TAX_IDENTIFIER) {
                return false;
            }
            return unmaskCnpj(pixKey.key) === unmaskCnpj(userDocument);
        });
    }, [pixKeys, userDocument, unmaskCnpj]);
    const keyTypeOptions = useMemo(() => [
        {
            type: PixType.EMAIL,
            label: t('pix-keys.email'),
            description: t('pix-keys.email-description'),
            isAlreadyRegistered: false,
        },
        {
            type: PixType.PHONE,
            label: t('pix-keys.phone'),
            description: t('pix-keys.phone-description'),
            isAlreadyRegistered: false,
        },
        {
            type: PixType.TAX_IDENTIFIER,
            label: t('pix-keys.cpf-cnpj'),
            description: t('pix-keys.cpf-cnpj-description'),
            isAlreadyRegistered: hasTaxIdentifierKey,
        },
        {
            type: PixType.RANDOM,
            label: t('pix-keys.random'),
            description: t('pix-keys.random-description'),
            isAlreadyRegistered: false,
        },
    ], [t, hasTaxIdentifierKey]);
    const handleKeyTypeClick = useCallback((type) => {
        const option = keyTypeOptions.find(opt => opt.type === type);
        if (option?.isAlreadyRegistered) {
            return;
        }
        setSelectedType(type);
        props?.onKeyTypeSelect?.(type);
    }, [keyTypeOptions, props]);
    const getDescriptionText = useCallback((option) => {
        const isDisabled = option.isAlreadyRegistered || false;
        return isDisabled ? t('pix-keys.already-registered') : option.description;
    }, [t]);
    const getOptionState = useCallback((type) => {
        const option = keyTypeOptions.find(opt => opt.type === type);
        return {
            isDisabled: option?.isAlreadyRegistered || false,
            isSelected: selectedType === type,
        };
    }, [keyTypeOptions, selectedType]);
    return {
        keyTypeOptions,
        handleKeyTypeClick,
        getDescriptionText,
        getOptionState,
    };
}
